import React, { useState, useRef } from 'react';
import { Tab, Tabs } from '@mui/material';
import { useDispatch } from 'react-redux';

import ListViewTable, {
  Ref as AlarmManagementListViewTableRef,
} from './ListViewTable';
import AlarmModuleService, {
  IAlarmModuleService,
} from 'services/AlarmModuleService';
import AlarmManagementPopup, { AlarmManagementPopupRef } from '../Popup/Popup';

import { AlarmRulesCount, AlertState } from 'types/types';
import { showAlert } from 'features/applicationState';

export type Tabs = 'All' | 'Active' | 'Paused';

export enum AlarmAction {
  DUPLICATE = 'Duplicate',
  PAUSE = 'Pause',
  ACTIVATE = 'Activate',
  DELETE = 'Delete',
}

interface Props {
  alarmsCount: AlarmRulesCount;
  fetchAlarmsCount: () => Promise<void>;
}
const ListView: React.FC<Props> = ({ alarmsCount, fetchAlarmsCount }) => {
  const tabs = [
    { title: `Active Alarms (${alarmsCount.ActiveAlarms})`, value: 'Active' },
    { title: `Paused Alarms (${alarmsCount.PausedAlarms})`, value: 'Paused' },
    { title: `All Alarms (${alarmsCount.AllAlarms})`, value: 'All' },
  ];

  const modalOptions = {
    [AlarmAction.DUPLICATE]: 'Are you sure you want to duplicate this alarm?',
    [AlarmAction.DELETE]: 'Are you sure you want to delete this alarm?',
    [AlarmAction.PAUSE]: 'Are you sure you want to pause this alarm?',
    [AlarmAction.ACTIVATE]: 'Are you sure you want to activate this alarm?',
  };

  const dispatch = useDispatch();

  const [modalType, setModalType] = useState<string>('');
  const [value, setValue] = useState<Tabs>('Active');

  const alarmManagementListViewTableRef =
    useRef<AlarmManagementListViewTableRef>(null);
  const modalRef = useRef<AlarmManagementPopupRef>(null);
  const alarmIdToApplyAction = useRef<number | undefined>(undefined);

  const refreshAlarmData = () => {
    try {
      const currentPage = alarmManagementListViewTableRef.current!.currentPage;

      const take = alarmManagementListViewTableRef.current!.take;

      fetchAlarmsCount();

      alarmManagementListViewTableRef.current?.fetchAlarmsData(
        currentPage * take,
        value
      );
    } catch (error) {
      console.error('Failed to fetch alarm definitions', error);
    }
  };

  const handleDeleteAlarmAction = () => {
    const niService: IAlarmModuleService = new AlarmModuleService();

    niService
      .DeleteAlarmDefinition(alarmIdToApplyAction.current!)
      .then(() => {
        refreshAlarmData();

        alarmIdToApplyAction.current = undefined;

        const alertData: AlertState = {
          severity: 'success',
          title: 'Alarm Deletion',
          message: 'Alarm has been deleted',
          open: true,
        };
        dispatch(showAlert(alertData));
      })
      .catch((error) => {
        const alertData: AlertState = {
          severity: 'error',
          title: 'Alarm Deletion',
          message: 'An error occured while trying to delete alarm',
          open: true,
        };
        dispatch(showAlert(alertData));
        console.error('Failed to delete alarm definitions', error);
      })
      .finally(() => {
        modalRef.current?.handleOpenPopup(false);
      });
  };
  const handleAlarmStatusChangeAction = (alarmStatus: boolean) => {
    const niService: IAlarmModuleService = new AlarmModuleService();

    niService
      .ChangeAlarmStatus(alarmIdToApplyAction.current!, alarmStatus)
      .then(() => {
        refreshAlarmData();

        alarmIdToApplyAction.current = undefined;

        const alertData: AlertState = {
          severity: 'success',
          title: 'Alarm Status',
          message: 'Alarm status has been updated',
          open: true,
        };
        dispatch(showAlert(alertData));
      })
      .catch((error) => {
        const alertData: AlertState = {
          severity: 'error',
          title: 'Alarm Status',
          message: "An error occured while trying to change alarm's status",
          open: true,
        };
        dispatch(showAlert(alertData));
        console.error("Failed to change alarm's status", error);
      })
      .finally(() => {
        modalRef.current?.handleOpenPopup(false);
      });
  };

  const handleSubmitModal = (action: string) => {
    switch (action) {
      case AlarmAction.DELETE:
        handleDeleteAlarmAction();
        break;
      case AlarmAction.PAUSE:
        handleAlarmStatusChangeAction(false);
        break;
      case AlarmAction.ACTIVATE:
        handleAlarmStatusChangeAction(true);
        break;
      default:
        break;
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: Tabs) => {
    setValue(newValue);
  };

  const handleCancelButtonPopupClick = () => {
    modalRef.current?.handleOpenPopup(false);
  };

  const handleCloseButtonPopupClick = () => {
    handleSubmitModal(modalType);
  };

  return (
    <div className="list-view-container">
      <Tabs
        value={value}
        onChange={handleChange}
        className="tabs-container"
        classes={{
          indicator: 'indicator-container',
        }}
      >
        {tabs.map((el) => (
          <Tab
            key={`${el.title}-${el.value}-tab`}
            value={el.value}
            label={el.title}
            className="tab-item"
          />
        ))}
      </Tabs>
      <div className="tab-content-container">
        <ListViewTable
          tabValue={value}
          modalRef={modalRef}
          setModalType={setModalType}
          ref={alarmManagementListViewTableRef}
          alarmIdToApplyAction={alarmIdToApplyAction}
        />
      </div>
      <AlarmManagementPopup
        ref={modalRef}
        title={modalOptions[modalType]}
        handleCloseButtonClick={handleCloseButtonPopupClick}
        handleCancelButtonClick={handleCancelButtonPopupClick}
      />
    </div>
  );
};

export default ListView;
